import React, { useState } from 'react';
import { Share2, MessageCircle, Mail, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '@/context/LangContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * One-tap share button for invitation links.
 *
 * On devices with the Web Share API (iOS/Android) it triggers the native share
 * sheet. Otherwise it opens a portal-rendered dropdown (no clipping) with
 * WhatsApp / Email / Copy fallback options.
 */
export default function ShareInviteButton({
  url,
  text = '',
  title,
  variant = 'default',
  className = '',
  'data-testid': testId,
}) {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);
  const hasNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const finalTitle = title || t('sib_default_title');
  const message = text || `${t('sib_default_text')} ${url}`;

  const native = async () => {
    try {
      await navigator.share({ title: finalTitle, text: message, url });
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error(t('sib_native_error'));
    }
  };

  const shareWhatsApp = () => {
    const u = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(u, '_blank', 'noopener,noreferrer');
  };

  const shareEmail = () => {
    const u = `mailto:?subject=${encodeURIComponent(finalTitle)}&body=${encodeURIComponent(message)}`;
    window.location.href = u;
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t('sib_link_copied'));
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t('sib_copy_failed'));
    }
  };

  const baseBtn =
    variant === 'primary'
      ? 'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-gold text-navy font-h uppercase tracking-wider text-sm font-semibold hover:bg-gold-light'
      : variant === 'compact'
      ? 'inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-gold/40 text-gold hover:bg-gold/10 text-xs font-h uppercase tracking-wider'
      : 'inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-gold/40 text-gold hover:bg-gold/10 text-sm font-h uppercase tracking-wider';

  // Native share path: one-tap, no dropdown
  if (hasNativeShare) {
    return (
      <button
        type="button"
        onClick={native}
        className={`${baseBtn} ${className}`}
        data-testid={testId || 'share-invite-btn'}
        title={t('sib_share_title')}
      >
        <Share2 className={variant === 'compact' ? 'w-3 h-3' : 'w-4 h-4'} />
        {variant !== 'compact' && <span>{t('sib_share')}</span>}
      </button>
    );
  }

  // Desktop / no-Web-Share fallback: portal dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`${baseBtn} ${className}`}
          data-testid={testId || 'share-invite-btn'}
          title={t('sib_share_title')}
        >
          <Share2 className={variant === 'compact' ? 'w-3 h-3' : 'w-4 h-4'} />
          {variant !== 'compact' && <span>{t('sib_share')}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-navy-deep border border-gold/40 text-white w-52"
        data-testid="share-invite-menu"
      >
        <DropdownMenuItem
          onClick={shareWhatsApp}
          className="cursor-pointer focus:bg-gold/10 focus:text-white"
          data-testid="share-invite-whatsapp"
        >
          <MessageCircle className="w-4 h-4 text-[#25D366] me-2" /> WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={shareEmail}
          className="cursor-pointer focus:bg-gold/10 focus:text-white"
          data-testid="share-invite-email"
        >
          <Mail className="w-4 h-4 text-gold/80 me-2" /> Email
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={copy}
          className="cursor-pointer focus:bg-gold/10 focus:text-white"
          data-testid="share-invite-copy"
        >
          {copied ? <Check className="w-4 h-4 text-emerald-400 me-2" /> : <Copy className="w-4 h-4 text-gold/80 me-2" />}
          {copied ? t('sib_copied') : t('sib_copy_link')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
